import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { notificar } from '../utils/notify';
import '../styles/Contabilidade.css';

// ── Componentes auxiliares fora do componente principal ──────────────────────
const FiltroHeader = ({ campo, valor, onChange, placeholder }) => (
  <input
    type="text"
    className="form-control form-control-sm mt-1"
    placeholder={placeholder || 'Filtrar...'}
    value={valor}
    onChange={e => onChange(campo, e.target.value)}
    style={{ fontSize: '0.75rem', minWidth: '80px' }}
    onClick={e => e.stopPropagation()}
  />
);

const FiltroSelect = ({ campo, valor, onChange, opcoes }) => (
  <select
    className="form-select form-select-sm mt-1"
    value={valor}
    onChange={e => onChange(campo, e.target.value)}
    style={{ fontSize: '0.75rem' }}
    onClick={e => e.stopPropagation()}
  >
    <option value="">Todos</option>
    {opcoes.map(v => <option key={v} value={v}>{v}</option>)}
  </select>
);

const CelulaEditavel = ({ registro, campo, editando, onIniciar, onSalvar, onCancelar, onMudar, inputRef }) => {
  const estaEditando = editando.id === registro.id && editando.campo === campo;
  const valor = registro[campo] || '';

  if (estaEditando) {
    return (
      <td style={{ minWidth: '140px', padding: '4px 8px' }}>
        <input
          ref={inputRef}
          type="text"
          className="form-control form-control-sm"
          value={editando.valor}
          onChange={e => onMudar(e.target.value)}
          onKeyDown={e => {
            if (e.key === 'Enter') onSalvar();
            if (e.key === 'Escape') onCancelar();
          }}
          onBlur={onSalvar}
          style={{ minWidth: '110px' }}
        />
      </td>
    );
  }

  return (
    <td
      style={{ minWidth: '140px', cursor: 'pointer' }}
      onClick={() => onIniciar(registro.id, campo, valor)}
      title="Clique para editar"
    >
      {valor ? (
        <span className={`badge ${campo === 'resp_fiscal' ? 'bg-info text-dark' : 'bg-warning text-dark'}`}>
          {valor}
        </span>
      ) : (
        <span className="text-muted small fst-italic">
          <i className="fas fa-pen fa-xs me-1"></i>clique para editar
        </span>
      )}
    </td>
  );
};
// ────────────────────────────────────────────────────────────────────────────

const Impostos_recup = () => {
  const [registros, setRegistros] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [mensagem, setMensagem] = useState('');
  useEffect(() => { notificar(mensagem); }, [mensagem]);

  // Filtros por coluna
  const [filtros, setFiltros] = useState({
    numero_dominio: '',
    cliente: '',
    regime_tributario: '',
    resp_contabil: '',
    resp_fiscal: '',
    resp_dp: '',
  });

  // Edição inline
  const [editando, setEditando] = useState({ id: null, campo: null, valor: '' });
  const inputEditRef = useRef(null);

  // Auth
  const [dadosUsuario, setDadosUsuario] = useState(null);
  const [carregandoAcesso, setCarregandoAcesso] = useState(true);
  const [acessoNegado, setAcessoNegado] = useState(false);

  // Modal adicionar
  const [mostrarModalAdicionar, setMostrarModalAdicionar] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [clientesContabil, setClientesContabil] = useState([]);
  const [buscaCliente, setBuscaCliente] = useState('');
  const [clienteSugestoes, setClienteSugestoes] = useState([]);
  const [mostrarSugestoes, setMostrarSugestoes] = useState(false);
  const sugestoesRef = useRef(null);
  const [form, setForm] = useState({
    numero_dominio: '',
    cliente: '',
    regime_tributario: '',
    resp_contabil: '',
    resp_fiscal: '',
    resp_dp: '',
  });

  // Modal histórico
  const [clienteSelecionado, setClienteSelecionado] = useState(null);
  const [mostrarModalHistorico, setMostrarModalHistorico] = useState(false);
  const [historico, setHistorico] = useState([]);
  const [carregandoHistorico, setCarregandoHistorico] = useState(false);
  const [novaMensagem, setNovaMensagem] = useState('');
  const [enviando, setEnviando] = useState(false);

  // ─── auth ───
  const verificarAcesso = useCallback(async () => {
    setCarregandoAcesso(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setDadosUsuario(res.data);
        setAcessoNegado(res.data.grupo === 'rh');
      } else {
        setAcessoNegado(true);
      }
    } catch {
      setAcessoNegado(true);
    } finally {
      setCarregandoAcesso(false);
    }
  }, []);

  useEffect(() => { verificarAcesso(); }, [verificarAcesso]);

  const username = dadosUsuario?.name || dadosUsuario?.first_name || dadosUsuario?.usuario || 'usuário';

  // ─── carregar registros ───
  const carregarRegistros = useCallback(async () => {
    setCarregando(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/impostos-recuperar/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistros(res.data.registros || []);
    } catch (err) {
      setMensagem(`Erro ao carregar: ${err.response?.data?.erro || err.message}`);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    if (!acessoNegado && !carregandoAcesso) carregarRegistros();
  }, [carregarRegistros, acessoNegado, carregandoAcesso]);

  // ─── clientes contábil (autocomplete) ───
  useEffect(() => {
    if (acessoNegado || carregandoAcesso) return;
    const token = localStorage.getItem('access');
    axios.get(`${process.env.REACT_APP_API_BASE}/api/empresas-contabil/`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => setClientesContabil(res.data.empresas || [])).catch(() => {});
  }, [acessoNegado, carregandoAcesso]);

  // Fechar sugestões ao clicar fora
  useEffect(() => {
    const handler = (e) => {
      if (sugestoesRef.current && !sugestoesRef.current.contains(e.target))
        setMostrarSugestoes(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // Focar input de edição ao abrir
  useEffect(() => {
    if (editando.id !== null && inputEditRef.current) {
      inputEditRef.current.focus();
      inputEditRef.current.select();
    }
  }, [editando.id, editando.campo]);

  // ─── autocomplete ───
  const filtrarClientes = (valor) => {
    if (!valor.trim()) return clientesContabil.slice(0, 50);
    const lower = valor.toLowerCase();
    return clientesContabil
      .filter(c =>
        c.empresa?.toLowerCase().includes(lower) ||
        c.numero_dominio?.toString().includes(lower)
      )
      .slice(0, 50);
  };

  const handleBuscaCliente = (valor) => {
    setBuscaCliente(valor);
    const sugestoes = filtrarClientes(valor);
    setClienteSugestoes(sugestoes);
    setMostrarSugestoes(sugestoes.length > 0);
  };

  const handleFocusBusca = () => {
    const sugestoes = filtrarClientes(buscaCliente);
    setClienteSugestoes(sugestoes);
    setMostrarSugestoes(sugestoes.length > 0);
  };

  const selecionarClienteAutoComplete = (c) => {
    setBuscaCliente(c.empresa);
    setMostrarSugestoes(false);
    setForm(prev => ({
      ...prev,
      numero_dominio: c.numero_dominio?.toString() || '',
      cliente: c.empresa || '',
      regime_tributario: c.regime || '',
      resp_contabil: c.operador || '',
    }));
  };

  const abrirModalAdicionar = () => {
    setBuscaCliente('');
    setForm({ numero_dominio: '', cliente: '', regime_tributario: '', resp_contabil: '', resp_fiscal: '', resp_dp: '' });
    setMostrarSugestoes(false);
    setMostrarModalAdicionar(true);
  };

  // ─── salvar novo ───
  const salvarRegistro = async () => {
    if (!form.cliente.trim()) { setMensagem('Selecione um cliente antes de salvar.'); return; }
    setSalvando(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/impostos-recuperar/`,
        form,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setMensagem(res.data.mensagem);
        setMostrarModalAdicionar(false);
        carregarRegistros();
      } else {
        setMensagem(res.data.mensagem || 'Erro ao salvar');
      }
    } catch (err) {
      setMensagem(err.response?.data?.mensagem || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  // ─── edição inline ───
  const iniciarEdicao = (id, campo, valorAtual) => {
    setEditando({ id, campo, valor: valorAtual || '' });
  };

  const cancelarEdicao = () => setEditando({ id: null, campo: null, valor: '' });

  const salvarEdicao = async () => {
    if (editando.id === null) return;
    const token = localStorage.getItem('access');
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_BASE}/api/impostos-recuperar/`,
        { id: editando.id, [editando.campo]: editando.valor },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistros(prev =>
        prev.map(r => r.id === editando.id ? { ...r, [editando.campo]: editando.valor } : r)
      );
    } catch {
      setMensagem('Erro ao salvar edição.');
    } finally {
      cancelarEdicao();
    }
  };

  const handleKeyEdicao = (e) => {
    if (e.key === 'Enter') salvarEdicao();
    if (e.key === 'Escape') cancelarEdicao();
  };

  // ─── finalizar ───
  const [finalizando, setFinalizando] = useState(null);

  const marcarFinalizado = async (id, cliente) => {
    if (!window.confirm(`Marcar "${cliente}" como finalizado? Ele sairá da lista.`)) return;
    setFinalizando(id);
    const token = localStorage.getItem('access');
    try {
      await axios.patch(
        `${process.env.REACT_APP_API_BASE}/api/impostos-recuperar/`,
        { id, finalizado: 'Sim' },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setRegistros(prev => prev.filter(r => r.id !== id));
      setMensagem(`"${cliente}" marcado como finalizado.`);
    } catch {
      setMensagem('Erro ao finalizar registro.');
    } finally {
      setFinalizando(null);
    }
  };

  // ─── histórico ───
  const abrirHistorico = async (registro) => {
    setClienteSelecionado(registro);
    setHistorico([]);
    setNovaMensagem('');
    setMostrarModalHistorico(true);
    setCarregandoHistorico(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.get(
        `${process.env.REACT_APP_API_BASE}/api/historico-imposto/${registro.numero_dominio}/`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setHistorico(res.data.historico || []);
    } catch {
      setMensagem('Erro ao carregar histórico');
    } finally {
      setCarregandoHistorico(false);
    }
  };

  const enviarMensagem = async () => {
    if (!novaMensagem.trim()) return;
    setEnviando(true);
    const token = localStorage.getItem('access');
    try {
      const res = await axios.post(
        `${process.env.REACT_APP_API_BASE}/api/historico-imposto/${clienteSelecionado.numero_dominio}/`,
        { mensagem: novaMensagem.trim(), cliente: clienteSelecionado.cliente, usuario_criador: username },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.data.success) {
        setHistorico(prev => [{
          id: res.data.id,
          cliente: clienteSelecionado.cliente,
          mensagem: novaMensagem.trim(),
          usuario_criador: username,
          criado_em: res.data.criado_em,
        }, ...prev]);
        setNovaMensagem('');
      }
    } catch {
      setMensagem('Erro ao salvar mensagem');
    } finally {
      setEnviando(false);
    }
  };

  // ─── filtro por coluna ───
  const setFiltro = (campo, valor) =>
    setFiltros(prev => ({ ...prev, [campo]: valor }));

  const registrosFiltrados = registros.filter(r => {
    return Object.entries(filtros).every(([campo, valor]) => {
      if (!valor.trim()) return true;
      const v = (r[campo] || '').toLowerCase();
      return v.includes(valor.toLowerCase());
    });
  });

  // Opções únicas para selects
  const unicos = (campo) =>
    [...new Set(registros.map(r => r[campo]).filter(Boolean))].sort();


  // ─── loading / acesso ───
  if (carregandoAcesso) {
    return (
      <div className="container mt-5 text-center">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '50vh' }}>
          <div>
            <div className="spinner-border text-primary mb-3" role="status"></div>
            <p className="text-muted">Verificando permissões...</p>
          </div>
        </div>
      </div>
    );
  }

  if (acessoNegado) {
    return (
      <div className="container mt-5 text-center">
        <div className="alert alert-danger">
          <i className="fas fa-lock fa-3x mb-3"></i>
          <h2>Acesso Negado</h2>
          <button className="btn btn-outline-primary mt-3" onClick={() => window.location.href = '/home'}>
            <i className="fas fa-home me-2"></i>Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="contabilidade-page">
      <div className="container mt-5 shadow p-4 rounded bg-white">

        {/* Cabeçalho */}
        <div className="header-wrapper d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex align-items-center">
            <i className="fas fa-file-invoice-dollar header-icon me-2"></i>
            <h2 className="titulo-pagina m-0">Impostos a Recuperar</h2>
          </div>
          <button className="btn btn-outline-secondary btn-sm" onClick={() => window.location.href = '/Contabilidade'}>
            <i className="fas fa-arrow-left me-2"></i>Voltar
          </button>
        </div>

        {/* Mensagens agora são exibidas como toast (react-toastify) */}

        {/* Barra de ações */}
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div className="d-flex gap-2 align-items-center">
            <button className="btn btn-outline-primary btn-sm" onClick={carregarRegistros} disabled={carregando}>
              <i className="fas fa-sync me-1"></i>Atualizar
            </button>
            {Object.values(filtros).some(v => v) && (
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={() => setFiltros({ numero_dominio: '', cliente: '', regime_tributario: '', resp_contabil: '', resp_fiscal: '', resp_dp: '' })}
              >
                <i className="fas fa-times me-1"></i>Limpar filtros
              </button>
            )}
          </div>
          <div className="d-flex gap-2 align-items-center">
            <span className="badge bg-success fs-6">
              <i className="fas fa-building me-1"></i>
              {registrosFiltrados.length} cliente(s)
            </span>
            <button className="btn btn-success" onClick={abrirModalAdicionar}>
              <i className="fas fa-plus me-2"></i>Adicionar Cliente
            </button>
          </div>
        </div>

        {/* Dica edição */}
        <div className="alert alert-info py-2 mb-3 d-flex align-items-center">
          <i className="fas fa-info-circle me-2"></i>
          <small>Clique nas células de <strong>Resp. Fiscal</strong> e <strong>Resp. DP</strong> para editar diretamente. Pressione <kbd>Enter</kbd> para salvar ou <kbd>Esc</kbd> para cancelar.</small>
        </div>

        {/* Tabela */}
        <div className="table-responsive">
          {carregando ? (
            <div className="text-center mt-4">
              <div className="spinner-border" role="status"></div>
              <p>Carregando...</p>
            </div>
          ) : registros.length === 0 ? (
            <div className="alert alert-info text-center mt-3">
              <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
              Nenhum cliente cadastrado. Clique em "Adicionar Cliente" para começar.
            </div>
          ) : (
            <table className="table table-bordered table-hover align-middle fs-6">
              <thead className="table-light">
                <tr>
                  <th style={{ minWidth: '90px' }}>
                    Cod. Domínio
                    <FiltroHeader campo="numero_dominio" valor={filtros.numero_dominio} onChange={setFiltro} placeholder="Filtrar..." />
                  </th>
                  <th style={{ minWidth: '180px' }}>
                    Cliente
                    <FiltroHeader campo="cliente" valor={filtros.cliente} onChange={setFiltro} placeholder="Filtrar..." />
                  </th>
                  <th style={{ minWidth: '130px' }}>
                    Regime Tributário
                    <FiltroSelect campo="regime_tributario" valor={filtros.regime_tributario} onChange={setFiltro} opcoes={unicos('regime_tributario')} />
                  </th>
                  <th style={{ minWidth: '130px' }}>
                    Resp. Contábil
                    <FiltroSelect campo="resp_contabil" valor={filtros.resp_contabil} onChange={setFiltro} opcoes={unicos('resp_contabil')} />
                  </th>
                  <th style={{ minWidth: '140px' }}>
                    Resp. Fiscal
                    <FiltroHeader campo="resp_fiscal" valor={filtros.resp_fiscal} onChange={setFiltro} placeholder="Filtrar..." />
                  </th>
                  <th style={{ minWidth: '140px' }}>
                    Resp. DP
                    <FiltroHeader campo="resp_dp" valor={filtros.resp_dp} onChange={setFiltro} placeholder="Filtrar..." />
                  </th>
                  <th style={{ width: '110px' }}>Histórico</th>
                  <th style={{ width: '140px' }}>Finalizar</th>
                </tr>
              </thead>
              <tbody>
                {registrosFiltrados.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted py-4">
                      Nenhum resultado para os filtros aplicados.
                    </td>
                  </tr>
                ) : registrosFiltrados.map(r => (
                  <tr key={r.id}>
                    <td className="text-center"><code>{r.numero_dominio || '-'}</code></td>
                    <td className="fw-semibold">
                      <i className="fas fa-building me-2 text-muted"></i>
                      {r.cliente}
                    </td>
                    <td className="text-center">
                      <span className="badge bg-secondary">{r.regime_tributario || '-'}</span>
                    </td>
                    <td className="text-center">
                      <span className="badge bg-primary">{r.resp_contabil || '-'}</span>
                    </td>
                    <CelulaEditavel
                      registro={r}
                      campo="resp_fiscal"
                      editando={editando}
                      onIniciar={iniciarEdicao}
                      onSalvar={salvarEdicao}
                      onCancelar={cancelarEdicao}
                      onMudar={valor => setEditando(prev => ({ ...prev, valor }))}
                      inputRef={inputEditRef}
                    />
                    <CelulaEditavel
                      registro={r}
                      campo="resp_dp"
                      editando={editando}
                      onIniciar={iniciarEdicao}
                      onSalvar={salvarEdicao}
                      onCancelar={cancelarEdicao}
                      onMudar={valor => setEditando(prev => ({ ...prev, valor }))}
                      inputRef={inputEditRef}
                    />
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-outline-primary"
                        onClick={() => abrirHistorico(r)}
                      >
                        <i className="fas fa-comments me-1"></i>Histórico
                      </button>
                    </td>
                    <td className="text-center">
                      <button
                        className="btn btn-sm btn-success"
                        onClick={() => marcarFinalizado(r.id, r.cliente)}
                        disabled={finalizando === r.id}
                        title="Marcar como finalizado"
                      >
                        {finalizando === r.id ? (
                          <span className="spinner-border spinner-border-sm"></span>
                        ) : (
                          <><i className="fas fa-check-circle me-1"></i>Finalizado</>
                        )}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modal Adicionar Cliente ── */}
      {mostrarModalAdicionar && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                  <i className="fas fa-plus-circle me-2"></i>Adicionar Cliente — Impostos a Recuperar
                </h5>
                <button type="button" className="btn-close" onClick={() => setMostrarModalAdicionar(false)}></button>
              </div>

              <div className="modal-body">
                <div className="mb-3" ref={sugestoesRef}>
                  <label className="form-label fw-bold">
                    <i className="fas fa-search me-1 text-success"></i>
                    Buscar cliente da contabilidade <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Digite o nome ou domínio do cliente..."
                    value={buscaCliente}
                    onChange={e => handleBuscaCliente(e.target.value)}
                    onFocus={handleFocusBusca}
                    autoComplete="off"
                  />
                  {mostrarSugestoes && clienteSugestoes.length > 0 && (
                    <div style={{
                      position: 'absolute', zIndex: 10050, background: 'white',
                      border: '1px solid #dee2e6', borderRadius: '0.375rem',
                      boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                      maxHeight: '220px', overflowY: 'auto', width: '55%',
                    }}>
                      {clienteSugestoes.map((c, i) => (
                        <div
                          key={i}
                          className="px-3 py-2 d-flex justify-content-between align-items-center"
                          style={{ cursor: 'pointer', borderBottom: '1px solid #f0f0f0' }}
                          onMouseEnter={e => e.currentTarget.style.background = '#f0f7ff'}
                          onMouseLeave={e => e.currentTarget.style.background = 'white'}
                          onClick={() => selecionarClienteAutoComplete(c)}
                        >
                          <span className="fw-semibold">{c.empresa}</span>
                          <span className="text-muted small ms-3">
                            Dom: <code>{c.numero_dominio}</code>
                            {c.regime && <> · {c.regime}</>}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                  {mostrarSugestoes && clienteSugestoes.length === 0 && (
                    <div className="mt-1 text-muted small">
                      <i className="fas fa-info-circle me-1"></i>Nenhum cliente encontrado.
                    </div>
                  )}
                </div>

                {form.cliente && (
                  <div className="alert alert-success py-2 mb-3">
                    <i className="fas fa-check-circle me-2"></i>
                    <strong>{form.cliente}</strong> — Dom: <code>{form.numero_dominio}</code>
                  </div>
                )}

                <div className="row g-3">
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-muted small">Cod. Domínio (automático)</label>
                    <input type="text" className="form-control" value={form.numero_dominio} readOnly disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold text-muted small">Regime Tributário (automático)</label>
                    <input type="text" className="form-control" value={form.regime_tributario} readOnly disabled />
                  </div>
                  <div className="col-md-12">
                    <label className="form-label fw-bold text-muted small">Resp. Contábil (automático)</label>
                    <input type="text" className="form-control" value={form.resp_contabil} readOnly disabled />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-user-tie me-1 text-info"></i>Resp. Fiscal
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nome do responsável fiscal"
                      value={form.resp_fiscal}
                      onChange={e => setForm(prev => ({ ...prev, resp_fiscal: e.target.value }))}
                    />
                  </div>
                  <div className="col-md-6">
                    <label className="form-label fw-bold">
                      <i className="fas fa-users me-1 text-warning"></i>Resp. DP
                    </label>
                    <input
                      type="text"
                      className="form-control"
                      placeholder="Nome do responsável DP"
                      value={form.resp_dp}
                      onChange={e => setForm(prev => ({ ...prev, resp_dp: e.target.value }))}
                    />
                  </div>
                </div>
              </div>

              <div className="modal-footer bg-light">
                <button type="button" className="btn btn-secondary" onClick={() => setMostrarModalAdicionar(false)}>
                  <i className="fas fa-times me-1"></i>Cancelar
                </button>
                <button type="button" className="btn btn-success" onClick={salvarRegistro} disabled={salvando || !form.cliente.trim()}>
                  {salvando
                    ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</>
                    : <><i className="fas fa-save me-1"></i>Salvar</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Histórico ── */}
      {mostrarModalHistorico && clienteSelecionado && (
        <div className="modal" tabIndex="-1" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999 }}>
          <div className="modal-dialog modal-lg modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content shadow-lg">
              <div className="modal-header" style={{ backgroundColor: '#ffffff', borderBottom: '1px solid #e9ecef' }}>
                <div>
                  <h5 className="modal-title mb-0 fw-bold" style={{ color: '#212529' }}>
                    <i className="fas fa-comments me-2"></i>Histórico de Conversa
                  </h5>
                  <small style={{ color: '#6c757d' }}>
                    <i className="fas fa-building me-1"></i>{clienteSelecionado.cliente}
                    {' · '}Dom: <code style={{ color: '#6c757d' }}>{clienteSelecionado.numero_dominio}</code>
                  </small>
                </div>
                <button type="button" className="btn-close" onClick={() => setMostrarModalHistorico(false)}></button>
              </div>

              <div className="modal-body" style={{ maxHeight: '420px', overflowY: 'auto' }}>
                {carregandoHistorico ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-success" role="status"></div>
                    <p className="mt-2 text-muted">Carregando histórico...</p>
                  </div>
                ) : historico.length === 0 ? (
                  <div className="alert alert-info text-center">
                    <i className="fas fa-inbox fa-2x mb-2 d-block"></i>
                    Nenhum registro ainda. Seja o primeiro!
                  </div>
                ) : (
                  <div className="d-flex flex-column gap-3">
                    {historico.map(item => (
                      <div key={item.id} className="card border-0 shadow-sm" style={{ borderLeft: '4px solid #198754' }}>
                        <div className="card-body py-2 px-3">
                          <div className="d-flex justify-content-between align-items-center mb-1">
                            <small className="fw-bold text-success">
                              <i className="fas fa-user-circle me-1"></i>
                              {item.usuario_criador || 'Usuário'}
                            </small>
                            <small className="text-muted">
                              <i className="fas fa-calendar-alt me-1"></i>{item.criado_em}
                            </small>
                          </div>
                          <p className="mb-0" style={{ whiteSpace: 'pre-wrap', fontSize: '0.95rem' }}>
                            {item.mensagem}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="modal-footer flex-column align-items-stretch gap-2 bg-light">
                <label className="fw-bold text-muted small mb-0">
                  <i className="fas fa-pen me-1"></i>Novo registro:
                </label>
                <textarea
                  className="form-control"
                  rows={3}
                  placeholder="Descreva o contato, andamento, combinado..."
                  value={novaMensagem}
                  onChange={e => setNovaMensagem(e.target.value)}
                  disabled={enviando}
                />
                <div className="d-flex justify-content-between">
                  <button type="button" className="btn btn-secondary btn-sm" onClick={() => setMostrarModalHistorico(false)}>
                    <i className="fas fa-times me-1"></i>Fechar
                  </button>
                  <button
                    type="button"
                    className="btn btn-success btn-sm"
                    onClick={enviarMensagem}
                    disabled={enviando || !novaMensagem.trim()}
                  >
                    {enviando
                      ? <><span className="spinner-border spinner-border-sm me-1"></span>Salvando...</>
                      : <><i className="fas fa-paper-plane me-1"></i>Salvar Registro</>}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Impostos_recup;
